import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  Button,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchEvents } from "@/store/eventsSlice";
import * as reviewsApi from "@api/reviews.api";
import { useAuth } from "@context/AuthContext";
import type { Review } from "@/types";
import { joinTextParts } from "@/utils/text";

export default function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: events, loading } = useAppSelector((s) => s.events);
  const { user } = useAuth();

  const [search, setSearch] = useState("");
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    dispatch(fetchEvents());
    if (user) {
      reviewsApi
        .getAll({ userId: user.id })
        .then(setReviews)
        .catch(() => {});
    }
  }, [dispatch, user]);

  const reviewedEventIds = new Set(reviews.map((r) => r.eventId));

  const filtered = events.filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.restaurant?.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const grouped = filtered.reduce<Record<string, typeof filtered>>(
    (acc, ev) => {
      const month = new Date(ev.startedAt).toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      if (!acc[month]) acc[month] = [];
      acc[month].push(ev);
      return acc;
    },
    {},
  );

  const pendingEvents = events.filter(
    (e) =>
      e.restaurantId &&
      !reviewedEventIds.has(e.id) &&
      new Date(e.startedAt) < new Date(),
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("history.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        {t("history.subtitle")}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={t("history.search")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />
      {pendingEvents.length > 0 && (
        <Card
          sx={{ mb: 3, borderLeft: "4px solid", borderColor: "warning.main" }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontWeight: 600,
              }}
            >
              {t("history.pendingReviews")} ({pendingEvents.length})
            </Typography>
            {pendingEvents.map((ev) => (
              <Box
                key={ev.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                }}
              >
                <Typography variant="body2">
                  {joinTextParts(ev.name, ev.restaurant?.name)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/events/${ev.id}/review`)}
                >
                  {t("events.submitReview")}
                </Button>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
      {loading ? (
        <Skeleton variant="rounded" height={200} />
      ) : Object.keys(grouped).length === 0 ? (
        <Typography
          sx={{
            color: "text.secondary",
            textAlign: "center",
            mt: 4,
          }}
        >
          {t("history.noHistory")}
        </Typography>
      ) : (
        Object.entries(grouped).map(([month, monthEvents]) => (
          <Box key={month} sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              {month}
            </Typography>
            {monthEvents.map((ev) => (
              <Card
                key={ev.id}
                sx={{ mb: 1.5, cursor: "pointer", "&:hover": { boxShadow: 4 } }}
                onClick={() => navigate(`/events/${ev.id}`)}
              >
                <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {ev.name}
                      </Typography>
                      {ev.restaurant && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <RestaurantIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {ev.restaurant.name}
                          </Typography>
                          {ev.restaurant.category && (
                            <Chip
                              label={ev.restaurant.category.name}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {new Date(ev.startedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                    <AvatarGroup
                      max={3}
                      sx={{
                        "& .MuiAvatar-root": {
                          width: 28,
                          height: 28,
                          fontSize: 12,
                        },
                      }}
                    >
                      {ev.eventUsers?.map((eu) => (
                        <Avatar key={eu.userId}>
                          {eu.user?.email?.[0]?.toUpperCase() || "?"}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ))
      )}
    </Box>
  );
}
